import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Divider, IconButton, Stack, Typography } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import _, { debounce } from 'lodash';

import { IConversation } from '../../../../../store/V2/ChatSlice';
import { typoGradientStyles } from '../../../../../helper';
import {
  adminChatGetConversationMessages,
  adminChatSendConversationMessage,
  adminChatSyncConversationMessages,
  clearConversationNotificationData,
  getAdminChatState,
} from '../../../../../store/V2/AdminChatSlice';
import { useAppSelector } from '../../../../../hooks';
import { TP_COLORS } from '../../../../../constants';
import { TPTextField } from '../../../../../components/Inputs';
import { getAuthState } from '../../../../../store/AuthSlice';
import { UnreadMessageElement } from '../../chatHelpers';
import BasicLoader from '../../../../../components/Loaders/BasicLoader';
import AdminChatMessageListItem from '../components/AdminChatMessageListItem';

interface AdminChatSelectedConversationViewProps {
  conversation: IConversation;
}

const AdminChatSelectedConversationView: FunctionComponent<AdminChatSelectedConversationViewProps> = ({
  conversation,
}) => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const adminChatState = useAppSelector(getAdminChatState);

  // Ref
  const stackRef = useRef<HTMLDivElement | null>(null);

  // State
  const [messageText, setMessageText] = useState('');
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isAutoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [isFirstTimePassed, setFirstTimePassed] = useState(false);

  const {
    adminChatConversationMessagesData,
    selectedConversation,
  } = adminChatState;

  const authUser = authState.user;
  const userCount = conversation?.users?.length ?? 0;
  const messagesList =
    adminChatConversationMessagesData.listData &&
    adminChatConversationMessagesData.listData.messages
      ? adminChatConversationMessagesData.listData.messages
      : [];

  let unreadMessageIndex = -1;

  useEffect(() => {
    dispatch(
      adminChatGetConversationMessages({
        conversationId: conversation._id,
        limit: 200,
        offset: 0,
      })
    );
  }, []);

  useEffect(() => {
    if (messagesList.length && isAutoScrollEnabled) {
      checkScroll();
    }
  }, [messagesList.length]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    const isBottom: boolean = scrollHeight - scrollTop - 10 < clientHeight;

    // if (scrollTop > lastScrollTop) {
    //   // Scroll Down
    // }

    if (scrollTop < lastScrollTop) {
      // Scroll Up
      setAutoScrollEnabled(false);
    }

    if (isBottom) {
      // Bottom Reached
      setAutoScrollEnabled(true);
      updateSeenStatus();
    }

    setLastScrollTop(scrollTop);
  };

  const scrollToBottom = (smoothScroll = false) => {
    const element = document.getElementById(
      `message-item-${messagesList.length - 1}`
    );

    if (element) {
      element.scrollIntoView({
        behavior: smoothScroll ? 'smooth' : undefined,
        block: 'end',
        inline: 'nearest',
      });
    }
  };

  const updateSeenStatus = debounce(() => {
    if (messagesList.length) {
      const messageIds = messagesList
        .filter(
          (messageItem) =>
            messageItem.sender._id !== authUser?._id &&
            messageItem.status.some(
              (item) => item.user === authUser?._id && item.status !== 'SEEN'
            )
        )
        .map((messageItem) => messageItem._id);

      if (messageIds.length) {
        dispatch(
          adminChatSyncConversationMessages({
            messageIds,
            status: 'SEEN',
          })
        );
      } else if (selectedConversation) {
        dispatch(clearConversationNotificationData(selectedConversation._id));
      }
    }
  }, 1000);

  const checkScroll = () => {
    if (stackRef.current) {
      const scrollOptions: ScrollIntoViewOptions = {
        behavior: isFirstTimePassed ? 'smooth' : undefined,
        block: 'nearest',
        inline: 'nearest',
      };

      let unReadElement: HTMLElement | null = null;

      if (unreadMessageIndex > -1) {
        unReadElement = window.document.getElementById(
          `message-item-${unreadMessageIndex}`
        );
      }

      // Scrollable
      if (stackRef.current.scrollHeight > stackRef.current.clientHeight) {
        if (unReadElement) {
          unReadElement.scrollIntoView(scrollOptions);
        } else {
          scrollToBottom(isFirstTimePassed);
        }
      } else {
        // Not scrollable
        updateSeenStatus();
      }

      setFirstTimePassed(true);
    }
  };

  const handleSendMessage = () => {
    if (selectedConversation) {
      dispatch(
        adminChatSendConversationMessage({
          conversationId: selectedConversation._id,
          message: messageText.trim(),
        })
      );

      setMessageText('');
    }
  };

  return (
    <>
      <Divider />
      <Stack
        ref={stackRef}
        spacing="10px"
        flexGrow="1"
        onScroll={handleScroll}
        sx={{
          overflowY: 'auto',
          padding: '16px 16px 0px 16px',
        }}
      >
        <Typography
          fontSize="26px"
          fontWeight="700"
          textAlign="center"
          sx={{ ...typoGradientStyles, padding: '40px 0px' }}
        >
          Connect, learn, and grow together!
        </Typography>
        {adminChatConversationMessagesData.isLoading ? <BasicLoader /> : null}
        {messagesList.map((messageItem, index) => {
          let unreadAlertElement: React.ReactElement | null = null;

          if (
            authUser &&
            unreadMessageIndex === -1 &&
            messageItem.status &&
            messageItem.status.some(
              (item) => item.user === authUser._id && item.status !== 'SEEN'
            )
          ) {
            unreadMessageIndex = index;
          }

          if (unreadMessageIndex === index) {
            unreadAlertElement = <UnreadMessageElement key="unreal-elem" />;
          }

          return [
            unreadAlertElement,
            <AdminChatMessageListItem
              key={`msg-item-${index}`}
              itemIndex={index}
              message={messageItem}
              conversationUsersCount={userCount}
            />,
          ];
        })}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          backgroundColor: TP_COLORS.listItemGray.light,
          borderRadius: '28px',
          padding: '11px',
          margin: '16px',
        }}
      >
        <TPTextField
          multiline
          fullWidth
          placeholder="Message"
          variant="standard"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={(e) => {
            // handleTyping();

            if (e.key === 'Enter' && !e.shiftKey && messageText.trim().length) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: '14px',
              marginLeft: '8px',
            },
          }}
        />

        <IconButton
          size="small"
          onClick={handleSendMessage}
          disabled={!messageText.trim().length}
        >
          <SendIcon sx={{ color: '#0D71D9' }} />
        </IconButton>
      </Stack>
    </>
  );
};

export default AdminChatSelectedConversationView;
